import * as React from 'react';

import type { LegacyContextType } from 'types/legacy-context-types';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import contextTypes from 'vendor/cnpm/fluxible.v0-4/lib/contextTypes';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import objectAssign from 'vendor/cnpm/object-assign.v3/index';

import hoistNonReactStatics from 'js/lib/hoistNonReactStatics';

/**
 * This file is a Coursera modification of Fluxible's connectToStores.
 *
 * Modification 1:
 * waitForStores allows the author to specify which stores need to be loaded before rendering a component.
 * For this to work, two things must be true:
 * 1. All stores must be loaded at the top-level (this connector does not actually load the stores).
 * 2. Each store must expose a `hasLoaded` function that returns true if the store is loaded
 * and false if not.
 *
 * When an array of store names is passed as the second parameter (as in stock connectToStores), waitForStores
 * waits for all of the specified stores to be loaded before rendering the wrapped component. It does so by
 * calling `hasLoaded()` on each of these stores.
 *
 * waitForStores also accepts an object of the following form as the second parameter:
 * {
 *   requiredStores: ['S12nStore'],
 *   lazyStores: ['CourseStore']
 * }
 * In this case, "requiredStores" must be loaded before rendering, and "lazyStores" do not have to be.
 *
 * Modification 2:
 * Fix bug where connectToStores handler would not run when props were updated.
 * Added componentWillReceiveProps method and props argument to getStateFromStores.
 *
 */

/**
 * Copyright 2015, Yahoo Inc.
 * Copyrights licensed under the New BSD License. See the accompanying LICENSE file for terms.
 */

('use strict');

/**
 * Registers change listeners and retrieves state from stores using `getStateFromStores`
 * method. Concept provided by Dan Abramov via
 * https://medium.com/@dan_abramov/mixins-are-dead-long-live-higher-order-components-94a0d2f9e750
 * @method connectToStores
 * @param {React.Component} Component component to pass state as props to
 * @param {array} stores List of stores to listen for changes
 * @param {function} getStateFromStores function that receives all stores and should return
 *      the full state object. Receives `stores` hash and component `props` as arguments
 * @returns {React.Component}
 */
function waitForStoresImpl(
  Component: $TSFixMe,
  stores: $TSFixMe,
  getStateFromStores: $TSFixMe
): React.ComponentType<$TSFixMe> {
  let requiredStores: $TSFixMe;
  let allStores: $TSFixMe = [];

  if (stores instanceof Array) {
    requiredStores = stores;
    allStores = stores;
  } else if (stores.hasOwnProperty('requiredStores') || stores.hasOwnProperty('lazyStores')) {
    requiredStores = stores.requiredStores || [];
    const lazyStores = stores.lazyStores || [];
    allStores = allStores.concat(requiredStores, lazyStores);
  }

  const componentName = Component.displayName || Component.name;

  class StoreConnector extends React.Component {
    static displayName = componentName + 'StoreConnector';

    static contextTypes = {
      getStore: contextTypes.getStore,
    };

    declare context: LegacyContextType<typeof StoreConnector.contextTypes>;

    componentDidMount() {
      // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'Store' implicitly has an 'any' type.
      allStores.forEach(function storesEach(Store) {
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        this.context.getStore(Store).addChangeListener(this._onStoreChange);
      }, this);
    }

    componentWillReceiveProps(nextProps: $TSFixMe) {
      this.setState(this.getStateFromStores(nextProps));
    }

    componentWillUnmount() {
      // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'Store' implicitly has an 'any' type.
      allStores.forEach(function storesEach(Store) {
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        this.context.getStore(Store).removeChangeListener(this._onStoreChange);
      }, this);
    }

    getStateFromStores = (_props: $TSFixMe) => {
      if (!this.haveRequiredStoresLoaded()) {
        return {};
      }

      const props = _props || this.props;
      if (typeof getStateFromStores === 'function') {
        const storeInstances = {};
        // @ts-expect-error ts-migrate(7006) FIXME: Parameter 'store' implicitly has an 'any' type.
        allStores.forEach(function (store) {
          const storeName = store.name || store.storeName || store;
          // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
          storeInstances[storeName] = this.context.getStore(store);
        }, this);
        return getStateFromStores(storeInstances, props);
      }
      const state = {};
      // @TODO deprecate?
      Object.keys(getStateFromStores).forEach(function (storeName) {
        const stateGetter = getStateFromStores[storeName];
        // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
        const store = this.context.getStore(storeName);
        objectAssign(state, stateGetter(store, props));
      }, this);
      return state;
    };

    _onStoreChange = () => {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
      this.setState(this.getStateFromStores());
    };

    haveRequiredStoresLoaded = () => {
      return requiredStores.every(
        function storesEach(Store: $TSFixMe) {
          // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
          const storeInstance = this.context.getStore(Store);
          if (!storeInstance.hasLoaded) {
            throw new Error(
              `waitForStores wrapping ${
                // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
                this.displayName
              }: hasLoaded is not defined for ${Store}.
 Define the method or, if the method cannot be defined, include the store in 'lazyStores' instead.`
            );
          } else {
            return storeInstance.hasLoaded();
          }
        }.bind(this)
      );
    };

    state = this.getStateFromStores(this.props);

    render() {
      if (!this.haveRequiredStoresLoaded()) {
        if (Component.LOADING_COMPONENT) {
          return React.createElement(Component.LOADING_COMPONENT);
        } else {
          return null;
        }
      }
      return React.createElement(Component, objectAssign({}, this.props, this.state));
    }
  }

  hoistNonReactStatics(StoreConnector, Component);

  return StoreConnector;
}

type StoresType<StoreNames> =
  | StoreNames[]
  | {
      requiredStores?: StoreNames[];
      lazyStores?: StoreNames[];
    };

function waitForStores<Props extends {}, InputProps extends {}, Stores>(
  stores: StoresType<keyof Stores>,
  connector: (stores: Stores, props: InputProps) => Omit<Props, keyof InputProps>
): (Component: React.ComponentType<Props>) => React.ComponentClass<InputProps>;

function waitForStores<Props extends {}, InputProps extends {}, Stores>(
  Component: React.ComponentType<Props>,
  stores: StoresType<keyof Stores>,
  connector: (stores: Stores, props: InputProps) => Omit<Props, keyof InputProps>
): React.ComponentClass<InputProps>;

// Not very safe version, when you don't pass Stores type.
function waitForStores<Props extends {}, InputProps extends {} = {}>(
  stores: StoresType<string>,
  connector: (stores: any, props: InputProps) => Omit<Props, keyof InputProps>
): (Component: React.ComponentType<Props>) => React.ComponentClass<InputProps, {}>;

function waitForStores<Props extends {}, InputProps extends {} = {}>(
  Component: React.ComponentType<Props>,
  stores: StoresType<string>,
  connector: (stores: any, props: InputProps) => Omit<Props, keyof InputProps>
): React.ComponentClass<InputProps>;

// Manually pass PropsFromStores if it is impossible to derive it from Props and InputProps.
function waitForStores<Props extends {}, InputProps extends {}, Stores, PropsFromStores extends {}>(
  stores: StoresType<keyof Stores>,
  connector: (stores: Stores, props: InputProps) => PropsFromStores
): (Component: React.ComponentType<Props>) => React.ComponentClass<InputProps>;

function waitForStores<Props extends {}, InputProps extends {}, Stores, PropsFromStores extends {}>(
  Component: React.ComponentType<Props>,
  stores: StoresType<keyof Stores>,
  connector: (stores: Stores, props: InputProps) => PropsFromStores
): React.ComponentClass<InputProps>;

function waitForStores(...arg: $TSFixMe[]) {
  const args = Array.prototype.slice.call(arg);
  if (args.length === 2) {
    const stores = args[0];
    const getStateFromStores = args[1];
    return function (Component: $TSFixMe) {
      return waitForStoresImpl(Component, stores, getStateFromStores);
    };
  }

  // @ts-expect-error ts-migrate(2683) FIXME: 'this' implicitly has type 'any' because it does n... Remove this comment to see the full error message
  return waitForStoresImpl.apply(this, arg);
}

export default waitForStores;
